import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Tooltip.scss";

const Tooltip = ({
  text,
  children,
  position = "bottom",
  open = true,
  followCursor = false,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisuallyOpen, setIsVisuallyOpen] = useState(false);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const [coords, setCoords] = useState({ top: 0, left: 0, arrowLeft: "50%" });
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeout = useRef(null);

  const handleMouseEnter = (e) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    if (followCursor) setMousePos({ x: e.clientX, y: e.clientY });

    setIsMounted(true);
    setTimeout(() => setIsVisuallyOpen(true), 10);
  };

  const handleMouseMove = (e) => {
    if (followCursor && isMounted) {
      setMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseLeave = () => {
    setIsVisuallyOpen(false);
    hideTimeout.current = setTimeout(() => {
      setIsMounted(false);
    }, 200);
  };

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (isMounted && targetRef.current && tooltipRef.current) {
        const targetRect = targetRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const margin = 12;

        let top = 0;
        let left = 0;
        let arrowLeft = "50%";

        let anchorX = followCursor
          ? mousePos.x
          : targetRect.left + targetRect.width / 2;

        left = anchorX - tooltipRect.width / 2;

        if (left + tooltipRect.width > window.innerWidth - margin) {
          left = window.innerWidth - tooltipRect.width - margin;
        }
        if (left < margin) {
          left = margin;
        }

        let arrowOffset = anchorX - left;
        arrowOffset = Math.max(
          15,
          Math.min(arrowOffset, tooltipRect.width - 15),
        );
        arrowLeft = `${arrowOffset}px`;

        if (position === "bottom") {
          top = (followCursor ? mousePos.y : targetRect.bottom) + 12;
        } else if (position === "top") {
          top =
            (followCursor ? mousePos.y : targetRect.top) -
            tooltipRect.height -
            12;
        } else if (position === "left") {
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.left - tooltipRect.width - 8;
          if (left < margin) left = margin;
        } else if (position === "right") {
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.right + 8;
          if (left + tooltipRect.width > window.innerWidth - margin) {
            left = window.innerWidth - tooltipRect.width - margin;
          }
        }

        if (top < margin) top = margin;
        if (top + tooltipRect.height > window.innerHeight - margin) {
          top = window.innerHeight - tooltipRect.height - margin;
        }

        setCoords({ top, left, arrowLeft });
      }
    };

    if (isMounted) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isMounted, position, text, mousePos, followCursor]);

  return (
    <div
      className="tooltip-container"
      ref={targetRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={(e) => handleMouseEnter(e)}
      onBlur={handleMouseLeave}
    >
      {children}
      {open &&
        isMounted &&
        createPortal(
          <div
            className={`tooltip tooltip-${position} ${isVisuallyOpen ? "is-visible" : ""}`}
            ref={tooltipRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              "--arrow-left": coords.arrowLeft,
            }}
          >
            <div className="tooltip-content">{text}</div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Tooltip;
